import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import {
  ContextStateType,
  ReducerAction,
  REDUCER_ACTION_TYPE,
  IOrgMetadata,
  UseContextType,
} from "./context.types";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import { PreferencesState } from "@pulse-ui/types/components/ToolPanel";

// In React 18 the FunctionalComponent interface has changed.
// The PropsWithChildren type is omitted from the props type
// of a FunctionalComponent after React 18, this means that
// we have to include the children prop ourseleves
interface Props {
  children: React.ReactNode;
}

const initState: ContextStateType = {
  account: {
    dropcopy_ip_addresses: [],
    user_id: "",
    authorized_portfolios: [],
    admin: false,
  },
  org_metadata: {
    org_id: "",
    display_name: "",
    created: "",
    last_modified: "",
    portfolios: [],
  },
  user_preferences: {
    quote_asset: null,
    interval: null,
    start_time: null,
  },
  selected_portfolio: null,
  selected_account: null,
  selected_quote_asset: null,
  selected_view: null,
  selected_symbol: null,
};

export const reducer = (
  state: ContextStateType,
  action: ReducerAction
): ContextStateType => {
  let new_state;

  switch (action.type) {
    case REDUCER_ACTION_TYPE.UPDATE_ORG_METADATA:
      new_state = {
        ...state,
        org_metadata: action.payload,
      };
      break;
    case REDUCER_ACTION_TYPE.SET_PORTFOLIO_BY_ID:
      if (!action.payload) new_state = { ...state, selected_portfolio: null };

      const portfolio: IPortfolioMetadata | undefined =
        state.org_metadata.portfolios.filter(
          (portfolio) => portfolio.portfolio_id === action.payload
        )[0];

      if (portfolio) {
        new_state = {
          ...state,
          selected_portfolio: portfolio,
        };
      } else {
        new_state = {
          ...state,
          selected_portfolio:
            action.payload === "all-portfolios"
              ? {
                  portfolio_id: "all-portfolios",
                  display_name: "All Portfolios",
                  created: "",
                  last_modified: "",
                  accounts: [],
                }
              : null,
        };
      }
      break;
    case REDUCER_ACTION_TYPE.SET_PORTFOLIO_BY_ACCOUNT_ID:
      if (!action.payload) new_state = { ...state, selected_portfolio: null };

      let new_selected_portfolio: IPortfolioMetadata | null = null;

      for (let portfolio of state.org_metadata.portfolios) {
        if (portfolio.accounts)
          for (let account of portfolio.accounts) {
            if (account.account_id === action.payload)
              new_selected_portfolio = portfolio;
          }
      }

      new_state = {
        ...state,
        selected_portfolio: new_selected_portfolio,
      };
      break;
    case REDUCER_ACTION_TYPE.SET_ACCOUNT_BY_ID:
      if (!action.payload) new_state = { ...state, selected_account: null };

      const account: IAccountMetadata | null =
        state.selected_portfolio &&
        state.selected_portfolio.accounts &&
        state.selected_portfolio.accounts.filter(
          (account) => account.account_id === action.payload
        )[0];

      if (account) {
        new_state = {
          ...state,
          selected_account: account,
        };
      } else {
        new_state = {
          ...state,
          selected_account:
            action.payload === "all-accounts"
              ? {
                  account_id: "all-accounts",
                  display_name: "All Accounts",
                  created: "",
                  last_modified: "",
                }
              : null,
        };
      }
      break;
    case REDUCER_ACTION_TYPE.SET_ACCOUNT_DROPCOPY_IP_ADDRESSES:
      new_state = {
        ...state,
        account: {
          ...state.account,
          dropcopy_ip_addresses: action.payload,
        },
      };
      break;
    case REDUCER_ACTION_TYPE.SET_QUOTE_ASSET:
      new_state = {
        ...state,
        selected_quote_asset: action.payload,
      };
      break;
    case REDUCER_ACTION_TYPE.SET_VIEW:
      new_state = {
        ...state,
        selected_view: action.payload,
      };
      break;
    case REDUCER_ACTION_TYPE.SET_ACCOUNT_USER_ID:
      new_state = {
        ...state,
        account: {
          ...state.account,
          user_id: action.payload,
        },
      };
      break;
    case REDUCER_ACTION_TYPE.SET_ACCOUNT_AUTHORIZED_PORTFOLIOS:
      new_state = {
        ...state,
        account: {
          ...state.account,
          authorized_portfolios: action.payload,
        },
      };
      break;
    case REDUCER_ACTION_TYPE.SET_ADMIN_STATUS:
      new_state = {
        ...state,
        account: {
          ...state.account,
          admin: action.payload,
        },
      };
      break;
    case REDUCER_ACTION_TYPE.SET_USER_PREFERENCES:
      new_state = {
        ...state,
        user_preferences:
          action.payload.preference_key === "all"
            ? action.payload.value
            : {
                ...state.user_preferences,
                [action.payload.preference_key]: action.payload.value,
              },
      };
      break;
    case REDUCER_ACTION_TYPE.SET_SYMBOL:
      new_state = {
        ...state,
        selected_symbol: action.payload,
      };
      break;
    default:
      new_state = state;
  }

  localStorage.setItem("pulse-ctx", JSON.stringify(new_state));
  return new_state;
};

const AppContext = createContext<UseContextType>({
  initState,
  context_state: initState,
  update_org_metadata: async () => ({ status: 100, error: null }),
  set_portfolio_by_id: async () => ({ status: 100, error: null }),
  set_portfolio_by_account_id: async () => ({ status: 100, error: null }),
  set_account_by_id: async () => ({ status: 100, error: null }),
  set_account_dropcopy_ip_addresses: async () => ({ status: 100, error: null }),
  set_quote_asset: async () => ({ status: 100, error: null }),
  set_view: async () => ({ status: 100, error: null }),
  set_account_user_id: async () => ({ status: 100, error: null }),
  set_account_authorized_portfolios: async () => ({ status: 100, error: null }),
  set_admin_status: async () => ({ status: 100, error: null }),
  set_user_preferences: async () => ({ status: 100, error: null }),
  set_symbol: async () => ({ status: 100, error: null }),
});

interface Props {
  children: React.ReactNode;
}

export const AppContextProvider: React.FC<Props> = ({ children }) => {
  const [context_state, dispatch] = useReducer(
    reducer,
    localStorage.getItem("pulse-ctx")
      ? JSON.parse(localStorage.getItem("pulse-ctx") || "{}")
      : initState
  );

  const update_org_metadata = useCallback(async (metadata: IOrgMetadata) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.UPDATE_ORG_METADATA,
          payload: metadata,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'update_org_metadata: ${error}`,
        });
      }
    });
  }, []);

  const set_portfolio_by_id = useCallback(async (id: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_PORTFOLIO_BY_ID,
          payload: id,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_portfolio_by_id: ${error}`,
        });
      }
    });
  }, []);

  const set_portfolio_by_account_id = useCallback(async (id: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_PORTFOLIO_BY_ACCOUNT_ID,
          payload: id,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_portfolio_by_account_id: ${error}`,
        });
      }
    });
  }, []);

  const set_account_by_id = useCallback(async (id: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_ACCOUNT_BY_ID,
          payload: id,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_account_by_id: ${error}`,
        });
      }
    });
  }, []);

  const set_account_dropcopy_ip_addresses = useCallback(
    async (metadata: string[]) => {
      return new Promise<{
        status: number;
        error: any;
      }>((resolve, reject) => {
        try {
          dispatch({
            type: REDUCER_ACTION_TYPE.SET_ACCOUNT_DROPCOPY_IP_ADDRESSES,
            payload: metadata,
          });

          resolve({
            status: 200,
            error: null,
          });
        } catch (error) {
          reject({
            status: 500,
            error: `Error dispatching 'set_account_dropcopy_ip_addresses: ${error}`,
          });
        }
      });
    },
    []
  );

  const set_quote_asset = useCallback(async (value: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_QUOTE_ASSET,
          payload: value,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_quote_asset: ${error}`,
        });
      }
    });
  }, []);

  const set_view = useCallback(async (value: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_VIEW,
          payload: value,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_view: ${error}`,
        });
      }
    });
  }, []);

  const set_account_user_id = useCallback(async (value: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_ACCOUNT_USER_ID,
          payload: value,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_account_user_id: ${error}`,
        });
      }
    });
  }, []);

  const set_account_authorized_portfolios = useCallback(
    async (value: string[] | null) => {
      return new Promise<{
        status: number;
        error: any;
      }>((resolve, reject) => {
        try {
          dispatch({
            type: REDUCER_ACTION_TYPE.SET_ACCOUNT_AUTHORIZED_PORTFOLIOS,
            payload: value,
          });

          resolve({
            status: 200,
            error: null,
          });
        } catch (error) {
          reject({
            status: 500,
            error: `Error dispatching 'set_account_authorized_portfolios: ${error}`,
          });
        }
      });
    },
    []
  );

  const set_admin_status = useCallback(async (value: boolean) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_ADMIN_STATUS,
          payload: value,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_admin_status: ${error}`,
        });
      }
    });
  }, []);

  const set_user_preferences = useCallback(
    async (
      preference_key: "all" | "quote_asset" | "interval" | "start_time",
      value: PreferencesState | string | null
    ) => {
      return new Promise<{
        status: number;
        error: any;
      }>((resolve, reject) => {
        try {
          dispatch({
            type: REDUCER_ACTION_TYPE.SET_USER_PREFERENCES,
            payload: {
              preference_key,
              value,
            },
          });

          resolve({
            status: 200,
            error: null,
          });
        } catch (error) {
          reject({
            status: 500,
            error: `Error dispatching 'set_user_preferences: ${error}`,
          });
        }
      });
    },
    []
  );

  const set_symbol = useCallback(async (value: string | null) => {
    return new Promise<{
      status: number;
      error: any;
    }>((resolve, reject) => {
      try {
        dispatch({
          type: REDUCER_ACTION_TYPE.SET_SYMBOL,
          payload: value,
        });

        resolve({
          status: 200,
          error: null,
        });
      } catch (error) {
        reject({
          status: 500,
          error: `Error dispatching 'set_symbol: ${error}`,
        });
      }
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      initState,
      context_state,
      update_org_metadata,
      set_portfolio_by_id,
      set_portfolio_by_account_id,
      set_account_by_id,
      set_account_dropcopy_ip_addresses,
      set_quote_asset,
      set_view,
      set_account_user_id,
      set_account_authorized_portfolios,
      set_admin_status,
      set_user_preferences,
      set_symbol,
    }),
    [
      context_state,
      update_org_metadata,
      set_portfolio_by_id,
      set_portfolio_by_account_id,
      set_account_by_id,
      set_account_dropcopy_ip_addresses,
      set_quote_asset,
      set_view,
      set_account_user_id,
      set_account_authorized_portfolios,
      set_admin_status,
      set_user_preferences,
      set_symbol,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(AppContext);
};

export default AppContextProvider;
